/**
 * Team → Appraisal record (founder, AD) and My day → My record (a member) — prototype VIEWS.record
 * (docs/prototype.html L2860): head actions; the Person dropdown (managers, one person) and the period select; the managers'
 * per-person table (On their watch … Not reviewed, the picked person highlighted); then the Kind dropdown and the
 * person's entries, beside "What went right" and "How entries are counted". An entry opens the full entry (openInc).
 *
 * Data: GET /team/record?user_id&period. Managers see everyone in `people`; a member's call returns only themselves.
 * The Kind dropdown (any set of kinds; t-da3a924a) filters the loaded entries (the counts ignore the kind, as the contract says). The summary is composed
 * on the page from the same payload (prototype appraisalText). "Import history" opens the history importer
 * (/tasks/history-import, `task.reassign`).
 *
 * Writes (prototype openInc actions, incModal, attrModal, disModal, respModal; t-31fbce66): "+ Log an incident"
 * (POST /incidents), "Review and decide the cause" / "Change the cause" (PATCH /incidents/{id}/cause, If-Match =
 * entry.version; a 412 reloads the record), "Dismiss" (POST /incidents/{id}/dismiss) — managers; "Add my side" /
 * "Edit my side" (POST /incidents/{id}/response) — the owner or the person it was attributed to. A dismissed entry
 * shows no actions (the server answers 409). "Show dismissed" lists only the dismissed entries (?dismissed=true).
 * No write control shows while viewing as someone (read-only).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { IncidentCause, IncidentKind, RecordCounts, RecordEntry, RecordPeriod, Severity, TeamRecordResponse, UserRef } from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { dayLabel, firstName, todayIso } from '../../lib/format';
import { useClients } from '../../lib/data';
import { useSession } from '../../lib/session';
import { useCreateIncident, useDecideIncidentCause, useDismissIncident, useRecordEntry, useRespondToIncident, useTeamRecord } from '../../lib/team';
import { Avatar, Drawer, Empty, ErrorState, Field, Loading, Modal, MultiSelectFilter, errorText, useFail, useToast } from '../../ui/kit';
import { Lbl } from '../../shell/Help';
import { HistoryImportModal } from '../work/HistoryImport';
import { COPY_FAILED, copyText } from '../../lib/clipboard';

/** Prototype IKIND, SEV, CAUSES. */
const IKIND: Record<IncidentKind, string> = {
  escalation: 'Escalation',
  missed: 'Missed deadline',
  late: 'Late response',
  slip: 'Date slipped',
  complaint: 'Client complaint',
  closure: 'Bad closure',
  mistake: 'Mistake',
};
const SEV: Record<Severity, [string, string]> = { high: ['cold', 'High'], med: ['warm', 'Medium'], low: ['', 'Low'] };
const CAUSES: Record<IncidentCause, [string, string]> = {
  person: ['cold', 'Our mistake'],
  process: ['warm', 'Process gap'],
  vendor: ['info', 'Vendor or platform'],
  client: ['', 'Client side'],
  none: ['', 'Unavoidable'],
  unclear: ['info', 'Not reviewed yet'],
};
const PERIODS: [RecordPeriod, string][] = [
  ['all', 'All time'],
  ['30', 'Last 30 days'],
  ['90', 'Last 90 days'],
  ['365', 'Last year'],
];

const KOLKATA_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' });
const KOLKATA_TIME = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false });
/** A timestamp → "Sat 17 Oct" on its Asia/Kolkata date (prototype dayLabel of a saved day). */
const atDay = (ts: string) => dayLabel(KOLKATA_DAY.format(new Date(ts)));
/** A timestamp → "Sat 17 Oct 14:05" (prototype trail `at`: dayLabel + nowT). */
const atStamp = (ts: string) => `${atDay(ts)} ${KOLKATA_TIME.format(new Date(ts))}`;
const who1 = (u: UserRef | null | undefined) => (u ? firstName(u.name) : 'Someone');

/** The person an entry is about besides its owner: the one a manager decided caused it (default the owner). */
const causedBy = (i: RecordEntry) => (i.cause === 'person' ? (i.caused_by ?? i.owner) : null);

type Act = { kind: 'cause' | 'dismiss' | 'respond'; entry: RecordEntry };

const howHere = (i: RecordEntry) => (i.source === 'history' ? 'Imported from old records' : i.is_auto ? 'Noticed by the system' : `Logged by ${i.raised_by ? firstName(i.raised_by.name) : 'a manager'}`);

/** Prototype appraisalText. */
export function appraisalText(r: TeamRecordResponse): string {
  const c = r.people.find((p) => p.user.id === r.person.id);
  const w = r.went_right;
  const t = [
    `Appraisal record: ${r.person.name}`,
    `Period: ${PERIODS.find(([k]) => k === r.period)?.[1] ?? r.period}`,
    '',
    `Incidents on their watch: ${c?.on_watch ?? 0}. Decided to be their own mistake: ${c?.caused ?? 0}. Not reviewed yet: ${c?.not_reviewed ?? 0}.`,
    `Escalations ${c?.escalations ?? 0}, missed deadlines ${c?.missed ?? 0}, late or slipped ${c?.late_or_slipped ?? 0}, client complaints ${c?.complaints ?? 0}.`,
    '',
    'Entries:',
  ];
  const L = [...r.entries].sort((a, b) => a.occurred_on.localeCompare(b.occurred_on));
  for (const i of L)
    t.push(
      `- ${dayLabel(i.occurred_on)}${i.client ? ', ' + i.client.name : ''}: ${i.title} [${IKIND[i.kind]}, ${SEV[i.severity][1]}, ${CAUSES[i.cause][1]}${i.cause === 'person' ? ` (${firstName((i.caused_by ?? i.owner)?.name ?? '')})` : ''}]${i.outcome ? ' Outcome: ' + i.outcome : ''}`,
    );
  if (!L.length) t.push('- None recorded');
  t.push(
    '',
    'What went right:',
    `- Tasks closed ${w.tasks_closed}, client tasks done ${w.client_tasks_kept}, escalations resolved ${w.escalations_resolved}, ideas live ${w.ideas_live}${w.history_closed ? `, old tasks closed on time ${w.history_closed_on_time} of ${w.history_closed}` : ''}.`,
  );
  return t.join('\n');
}

export function AppraisalRecord() {
  const { can, me, readOnly } = useSession();
  const lead = can('team.read');
  /** Managers write the record; nobody writes while viewing as someone. */
  const leadWrites = lead && !readOnly;
  const toast = useToast();
  const [who, setWho] = useState<string | undefined>(undefined);
  const [period, setPeriod] = useState<RecordPeriod>('all');
  /** Picked kinds; empty = All. On screen over the loaded entries (GET /team/record is not paged). */
  const [kind, setKind] = useState<IncidentKind[]>([]);
  const [dis, setDis] = useState(false);
  const [open, setOpen] = useState<RecordEntry | null>(null);
  const [act, setAct] = useState<Act | null>(null);
  const [logging, setLogging] = useState(false);
  const [summary, setSummary] = useState(false);
  const [importing, setImporting] = useState(false);
  const q = useTeamRecord({ user_id: lead ? who : undefined, period, dismissed: lead && dis });
  const entries = useMemo(() => (q.data?.entries ?? []).filter((e) => !kind.length || kind.includes(e.kind)), [q.data, kind]);

  // Deep link ?incident=<id> (Review queue "Decide the cause", Work, Today): GET /incidents/{id} reads the entry
  // itself. One on someone's record shows that person (owner, else the person it was put on) with the entry open, so
  // "Review and decide the cause" is one click away. One with no owner and no cause decided is on nobody's record: it
  // opens on its own under "Unassigned" (no person picked) and the link stays, so the entry stays fresh; once the
  // cause is decided (a person, caused_by_id required) it lands on that person's record and the page shows them.
  const [sp, setSp] = useSearchParams();
  const deepId = sp.get('incident');
  const deep = useRecordEntry(deepId);
  const [deepMiss, setDeepMiss] = useState<string | null>(null);
  const shown = useRef<string | null>(null);
  const loose = deepId && deep.data && !deep.data.owner && !deep.data.caused_by ? deep.data : null;
  useEffect(() => {
    if (!deepId) {
      shown.current = null;
      return;
    }
    const clear = () =>
      setSp(
        (x) => {
          x.delete('incident');
          return x;
        },
        { replace: true },
      );
    if (deep.error) {
      const st = deep.error instanceof ApiFailure ? deep.error.status : 0;
      setDeepMiss(
        st === 404
          ? lead
            ? 'That entry does not exist any more.'
            : 'That entry is not on your record, or it does not exist any more.'
          : st === 422
            ? 'That link does not point to an entry.'
            : `That entry could not be opened: ${errorText(deep.error)}`,
      );
      clear();
      return;
    }
    const e = deep.data;
    if (!e) return;
    setDeepMiss(null);
    const first = shown.current !== deepId;
    shown.current = deepId;
    const whose = e.owner ?? e.caused_by;
    if (!whose) {
      // Unassigned: open it on its own; the record below stays as it is.
      if (first) setOpen(e);
      return;
    }
    if (lead) setWho(whose.id);
    setPeriod('all');
    setKind([]);
    setDis(lead && !!e.dismissed);
    // Opened as unassigned and just decided: show the person's record, without reopening the entry.
    if (first) setOpen(e);
    clear();
  }, [deepId, deep.data, deep.error, lead, setSp]);
  const closeLoose = () =>
    setSp(
      (x) => {
        x.delete('incident');
        return x;
      },
      { replace: true },
    );

  if (q.isPending) return <Loading label="Loading the record…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const r = q.data;
  const p = r.person;
  const mine = r.people.find((x) => x.user.id === p.id);
  const w = r.went_right;

  return (
    <>
      {deepId && deep.isPending ? (
        <div className="note" role="status" style={{ marginBottom: 12 }}>
          Finding that entry…
        </div>
      ) : deepMiss ? (
        <div className="note warn" role="alert" style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{deepMiss}</span>
          <button className="btn sm ghost" onClick={() => setDeepMiss(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
        <div className="r" style={{ marginLeft: 0 }}>
          {lead && can('task.reassign') ? (
            <button className="btn" onClick={() => setImporting(true)}>
              Import history
            </button>
          ) : null}
          {leadWrites ? (
            <button className="btn" onClick={() => setLogging(true)}>
              + Log an incident
            </button>
          ) : null}
          <button className="btn pri" onClick={() => setSummary(true)}>
            {lead ? 'Appraisal summary' : 'See my summary'}
          </button>
        </div>
      </div>
      {loose ? (
        <div className="panel" style={{ marginBottom: 14 }} aria-label="Unassigned entry">
          <div className="hd">
            <h3>Unassigned</h3>
            <span className="sub" style={{ fontSize: 12.5 }}>
              No owner and the cause is not decided, so it is on nobody’s record yet.
            </span>
            <div className="r">
              <button className="btn sm ghost" onClick={closeLoose}>
                Close
              </button>
            </div>
          </div>
          <EntryRow i={loose} onOpen={() => setOpen(loose)} />
        </div>
      ) : null}
      <div className="bar">
        {lead ? (
          // One person's record at a time, so single and always one picked (no All). The ?incident= deep link sets it.
          <MultiSelectFilter
            label="Person"
            single
            required
            options={r.people.map((o) => ({ value: o.user.id, label: o.user.name, count: o.not_reviewed ? `${o.not_reviewed} not reviewed` : undefined }))}
            value={[p.id]}
            onChange={(v) => v[0] && setWho(v[0])}
          />
        ) : null}
        <span className="grow" />
        <select className="in" style={{ width: 'auto', padding: '5px 8px' }} value={period} onChange={(e) => setPeriod(e.target.value as RecordPeriod)} aria-label="Period">
          {PERIODS.map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </div>
      {lead ? (
        <div className="panel" style={{ marginBottom: 14 }}>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th><Lbl>Person</Lbl></th>
                  <th className="tr"><Lbl>On their watch</Lbl></th>
                  <th className="tr"><Lbl>Caused by them</Lbl></th>
                  <th className="tr"><Lbl>Escalations</Lbl></th>
                  <th className="tr"><Lbl>Missed deadlines</Lbl></th>
                  <th className="tr"><Lbl>Late or slipped</Lbl></th>
                  <th className="tr"><Lbl>Complaints</Lbl></th>
                  <th className="tr"><Lbl>Not reviewed</Lbl></th>
                </tr>
              </thead>
              <tbody>
                {r.people.map((o: RecordCounts) => (
                  <tr key={o.user.id} className="row" onClick={() => setWho(o.user.id)} style={o.user.id === p.id ? { background: 'var(--brand-bg, #eef2ff)' } : undefined}>
                    <td>
                      <span className="split" style={{ gap: 6 }}>
                        <Avatar user={o.user} /> <b>{firstName(o.user.name)}</b>
                      </span>
                    </td>
                    <td className="tr num">{o.on_watch}</td>
                    <td className="tr num">
                      <b>{o.caused}</b>
                    </td>
                    <td className="tr num">{o.escalations}</td>
                    <td className="tr num">{o.missed}</td>
                    <td className="tr num">{o.late_or_slipped}</td>
                    <td className="tr num">{o.complaints}</td>
                    <td className="tr num">{o.not_reviewed ? <span className="chip cold">{o.not_reviewed}</span> : '0'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      <div className="cols c21">
        <div>
          <div className="bar" style={{ marginBottom: 8 }}>
            <MultiSelectFilter<IncidentKind>
              label="Kind"
              options={(Object.keys(IKIND) as IncidentKind[]).map((k) => ({ value: k, label: IKIND[k], count: (q.data?.entries ?? []).filter((e) => e.kind === k).length }))}
              value={kind}
              onChange={setKind}
            />
          </div>
          <div className="panel">
            <div className="hd">
              <h3>
                {p.name}: {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
              </h3>
              {lead ? (
                <div className="r">
                  <button className="btn sm ghost" onClick={() => setDis((x) => !x)} aria-pressed={dis}>
                    {dis ? 'Hide dismissed' : 'Show dismissed'}
                  </button>
                </div>
              ) : null}
            </div>
            {lead && mine?.not_reviewed ? (
              <div className="note warn" style={{ margin: '12px 16px' }}>
                <b>{mine.not_reviewed} not reviewed yet.</b> Say whether each was a mistake, a process gap, a vendor or the client, so it is counted fairly.
              </div>
            ) : null}
            {entries.length ? (
              entries.map((i) => (
                <EntryRow key={i.id} i={i} onOpen={() => setOpen(i)} />
              ))
            ) : (
              <Empty title="Nothing recorded">{dis ? 'No dismissed entries for this filter.' : 'No entries for this filter.'}</Empty>
            )}
          </div>
        </div>
        <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
          <div className="panel">
            <div className="hd">
              <h3><Lbl>What went right</Lbl></h3>
            </div>
            <div className="bd" style={{ display: 'grid', gap: 8, fontSize: 13.5 }}>
              {(
                [
                  ['Tasks closed', w.tasks_closed],
                  ['Client tasks done', w.client_tasks_kept],
                  ['Escalations resolved', w.escalations_resolved],
                  ['Ideas that went live', w.ideas_live],
                ] as [string, number][]
              ).map(([l, v]) => (
                <div className="split" key={l}>
                  <span>{l}</span>
                  <div className="grow" />
                  <b className="num">{v}</b>
                </div>
              ))}
              {w.history_closed ? (
                <div className="split">
                  <span>Old tasks closed on time</span>
                  <div className="grow" />
                  <b className="num">
                    {w.history_closed_on_time} of {w.history_closed}
                  </b>
                </div>
              ) : null}
              <div className="sub" style={{ fontSize: 12 }}>
                Shown beside the problems so an appraisal sees both.
              </div>
            </div>
          </div>
          <div className="panel">
            <div className="hd">
              <h3><Lbl>How entries are counted</Lbl></h3>
            </div>
            <div className="bd sub" style={{ fontSize: 12.5, display: 'grid', gap: 6 }}>
              <div>
                <b>On their watch:</b> it happened on their client or task.
              </div>
              <div>
                <b>Caused by them:</b> the Ops associate director decided it was their mistake, not a vendor, the client or a gap in the process.
              </div>
              <div>Dismissing an entry needs a reason. It is never deleted.</div>
            </div>
          </div>
        </div>
      </div>
      {open ? (
        <EntryDrawer
          i={open}
          lead={leadWrites}
          canRespond={!readOnly && (open.owner?.id === me.user.id || causedBy(open)?.id === me.user.id)}
          onAct={(k) => {
            setAct({ kind: k, entry: open });
            setOpen(null);
          }}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {act?.kind === 'cause' ? <CauseModal i={act.entry} people={r.people.map((x) => x.user)} onClose={() => setAct(null)} /> : null}
      {act?.kind === 'dismiss' ? <DismissModal i={act.entry} onClose={() => setAct(null)} /> : null}
      {act?.kind === 'respond' ? <RespondModal i={act.entry} onClose={() => setAct(null)} /> : null}
      {logging ? <LogIncidentModal people={r.people.map((x) => x.user)} onClose={() => setLogging(false)} /> : null}
      {summary ? (
        <Modal
          wide
          title="Appraisal summary"
          sub={`${p.name}. Copy it into the appraisal.`}
          onClose={() => setSummary(false)}
          footer={
            <>
              <button
                className="btn"
                onClick={() => {
                  copyText(appraisalText(r), () => toast('Copied'), () => toast(COPY_FAILED));
                }}
              >
                Copy
              </button>
              <div className="grow" />
              <button className="btn pri" onClick={() => setSummary(false)}>
                Close
              </button>
            </>
          }
        >
          <textarea className="in" rows={16} readOnly style={{ fontSize: 12.5 }} value={appraisalText(r)} aria-label="Appraisal summary" />
        </Modal>
      ) : null}
      {importing ? <HistoryImportModal onClose={() => setImporting(false)} /> : null}
    </>
  );
}

/** One entry in a list (a person's entries, or the Unassigned one): kind, title, when, how it is theirs, chips. */
function EntryRow({ i, onOpen }: { i: RecordEntry; onOpen: () => void }) {
  const how = !i.owner && !i.caused_by ? 'No owner yet' : i.relation === 'on_watch' ? 'On their watch' : 'Caused by them';
  return (
    <div className="it" onClick={onOpen} role="button" tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' ? onOpen() : undefined)}>
      <span className="kd">{IKIND[i.kind]}</span>
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="tt">{i.title}</div>
        <div className="mm">
          {dayLabel(i.occurred_on)}
          {i.client ? `. ${i.client.name}` : ''}. {how}
          {i.source === 'history' ? '. Imported' : i.is_auto ? '. Noticed by the system' : ''}
        </div>
      </div>
      <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {i.response ? <span className="chip info">Their side added</span> : null}
        <span className={`chip ${SEV[i.severity][0]}`}>{SEV[i.severity][1]}</span>
        <span className={`chip ${CAUSES[i.cause][0]}`}>{CAUSES[i.cause][1]}</span>
        {i.status === 'open' ? <span className="chip warm">Open</span> : null}
        {i.dismissed ? <span className="chip">Dismissed</span> : null}
      </span>
    </div>
  );
}

function EntryDrawer({ i, lead, canRespond, onAct, onClose }: { i: RecordEntry; lead: boolean; canRespond: boolean; onAct: (k: Act['kind']) => void; onClose: () => void }) {
  const live = !i.dismissed;
  const about = i.owner ?? i.caused_by;
  return (
    <Drawer
      title={about ? IKIND[i.kind] : `Unassigned · ${IKIND[i.kind]}`}
      onClose={onClose}
    >
      {!about ? (
        <div className="note warn" style={{ margin: '0 0 12px' }}>
          <b>On nobody’s record yet.</b> It has no owner and the cause is not decided. Decide the cause to put it on someone’s record.
        </div>
      ) : null}
      <h2 style={{ fontSize: 18, marginBottom: 10 }}>{i.title}</h2>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className={`chip ${SEV[i.severity][0]}`}>{SEV[i.severity][1]}</span>
        <span className={`chip ${CAUSES[i.cause][0]}`}>{CAUSES[i.cause][1]}</span>
        <span className={`chip ${i.status === 'open' ? 'warm' : 'hot'}`}>{i.status === 'open' ? 'Open' : 'Closed'}</span>
        {i.dismissed ? <span className="chip">Dismissed</span> : null}
      </div>
      <dl className="kv">
        <dt>Date</dt>
        <dd>{dayLabel(i.occurred_on)}</dd>
        <dt>Client</dt>
        <dd>
          {i.client ? (
            <>
              <Link to={`/clients/book?client=${encodeURIComponent(i.client.id)}`} style={{ color: 'var(--brand)', fontWeight: 600, textDecoration: 'none' }}>
                {i.client.name}
              </Link>
              {i.client.priority === 'high' ? (
                <span className="pm" title="High priority client">
                  {' '}▲
                </span>
              ) : null}
            </>
          ) : (
            'Internal'
          )}
        </dd>
        <dt>On whose watch</dt>
        <dd>{i.owner ? i.owner.name : 'No owner'}</dd>
        <dt>Caused by</dt>
        <dd>{i.cause === 'person' ? (causedBy(i)?.name ?? '-') : CAUSES[i.cause][1]}</dd>
        <dt>How it got here</dt>
        <dd>{howHere(i)}</dd>
        {i.closed_on ? (
          <>
            <dt>Closed</dt>
            <dd>{dayLabel(i.closed_on)}</dd>
          </>
        ) : null}
      </dl>
      {i.note ? (
        <>
          <div className="lbl" style={{ margin: '18px 0 6px' }}>
            What went wrong
          </div>
          <div style={{ fontSize: 13.5 }}>{i.note}</div>
        </>
      ) : null}
      {i.outcome ? (
        <>
          <div className="lbl" style={{ margin: '18px 0 6px' }}>
            Outcome
          </div>
          <div style={{ fontSize: 13.5 }}>{i.outcome}</div>
        </>
      ) : null}
      <div className="lbl" style={{ margin: '18px 0 6px' }}>
        {about ? `${who1(about)}’s side` : 'Their side'}
      </div>
      {i.response ? (
        <div className="note" style={{ margin: 0 }}>
          <span style={{ whiteSpace: 'pre-wrap' }}>{i.response.text}</span>
          <div className="sub" style={{ marginTop: 4, fontSize: 12 }}>
            {who1(i.response.by)}, {atDay(i.response.at)}
          </div>
        </div>
      ) : (
        <div className="sub" style={{ fontSize: 13 }}>
          Nothing added yet.
        </div>
      )}
      {i.dismissed ? (
        <>
          <div className="lbl" style={{ margin: '18px 0 6px' }}>
            Dismissed
          </div>
          <div className="note warn" style={{ margin: 0 }}>
            {who1(i.dismissed.by)}: {i.dismissed.reason}
          </div>
        </>
      ) : null}
      <div className="lbl" style={{ margin: '18px 0 8px' }}>
        Trail
      </div>
      <div style={{ fontSize: 13, color: 'var(--sub)', display: 'grid', gap: 5 }}>
        {i.trail.length ? (
          i.trail.map((l, n) => (
            <div key={n}>
              {atStamp(l.at)}: {l.text} <span className="sub">({who1(l.by)})</span>
            </div>
          ))
        ) : (
          <div>No changes yet</div>
        )}
      </div>
      <div className="split" style={{ marginTop: 22, flexWrap: 'wrap' }}>
        {lead && live ? (
          <>
            <button className="btn pri" onClick={() => onAct('cause')}>
              {i.cause === 'unclear' ? 'Review and decide the cause' : 'Change the cause'}
            </button>
            <button className="btn bad" onClick={() => onAct('dismiss')}>
              Dismiss
            </button>
          </>
        ) : null}
        {canRespond && live ? (
          <button className={`btn${lead ? '' : ' pri'}`} onClick={() => onAct('respond')}>
            {i.response ? 'Edit my side' : 'Add my side'}
          </button>
        ) : null}
        <button className="btn" onClick={onClose}>
          Close
        </button>
      </div>
    </Drawer>
  );
}

/** A 412 on an entry: someone changed it since it was opened. The record reloads (the hooks refresh it). */
const stale = (e: unknown) => e instanceof ApiFailure && e.status === 412;

/** Prototype attrModal: "What caused this?" */
function CauseModal({ i, people, onClose }: { i: RecordEntry; people: UserRef[]; onClose: () => void }) {
  const m = useDecideIncidentCause();
  const { errs, setErrs, fail, toast } = useFail(['cause', 'caused_by_id', 'severity', 'note', 'outcome']);
  const [cause, setCause] = useState<Exclude<IncidentCause, 'unclear'>>(i.cause === 'unclear' ? 'person' : i.cause);
  // No owner to default to on an unassigned entry: the manager picks the person (the server 422s without one).
  const [whose, setWhose] = useState((i.caused_by ?? i.owner)?.id ?? '');
  const [sev, setSev] = useState<Severity>(i.severity);
  const [note, setNote] = useState(i.note ?? '');
  const [outcome, setOutcome] = useState(i.outcome ?? '');
  const opts = people.some((u) => u.id === whose) || !whose ? people : [...people, ...[i.caused_by, i.owner].filter((u): u is UserRef => !!u && u.id === whose)];
  const save = () => {
    setErrs({});
    const n = note.trim();
    if (cause === 'person' && !whose) {
      setErrs({ caused_by_id: 'Pick whose mistake it was. This entry has no owner to put it on' });
      return;
    }
    if (cause === 'person' && !n) {
      setErrs({ note: 'Say what went wrong, so the person can see it' });
      return;
    }
    m.mutate(
      { id: i.id, version: i.version, body: { cause, caused_by_id: cause === 'person' ? whose || null : null, severity: sev, note: n, outcome: outcome.trim() } },
      {
        onSuccess: (r) => {
          const now = !i.owner && !i.caused_by ? r.caused_by : null;
          toast(now ? `Saved. It is on ${firstName(now.name)}’s record now.` : 'Saved. The person can see this entry.');
          onClose();
        },
        onError: (e) => {
          if (stale(e)) {
            toast(errorMessage(e));
            onClose();
          } else fail(e);
        },
      },
    );
  };
  return (
    <Modal
      title="What caused this?"
      sub={
        <>
          {i.title}
          <br />
          <span className="sub">
            {i.owner ? `On ${i.owner.name}’s watch` : 'No owner (on nobody’s record yet)'}, {dayLabel(i.occurred_on)}
          </span>
        </>
      }
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={m.isPending}>
            {m.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <Field label="Cause" error={errs.cause}>
        <select className="in" value={cause} onChange={(e) => setCause(e.target.value as Exclude<IncidentCause, 'unclear'>)}>
          {(Object.keys(CAUSES) as IncidentCause[])
            .filter((k) => k !== 'unclear')
            .map((k) => (
              <option key={k} value={k}>
                {CAUSES[k][1]}
              </option>
            ))}
        </select>
      </Field>
      {cause === 'person' ? (
        <Field label="Whose mistake" error={errs.caused_by_id}>
          <select className="in" value={whose} onChange={(e) => setWhose(e.target.value)}>
            {!whose ? <option value="">Pick a person</option> : null}
            {opts.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <div className="cols c2">
        <Field label="How serious" error={errs.severity}>
          <select className="in" value={sev} onChange={(e) => setSev(e.target.value as Severity)}>
            {(['high', 'med', 'low'] as Severity[]).map((k) => (
              <option key={k} value={k}>
                {SEV[k][1]}
              </option>
            ))}
          </select>
        </Field>
        <div />
      </div>
      <Field label="What went wrong" error={errs.note}>
        <textarea className="in" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
      </Field>
      <Field label="Outcome" error={errs.outcome}>
        <input className="in" value={outcome} onChange={(e) => setOutcome(e.target.value)} maxLength={2000} />
      </Field>
    </Modal>
  );
}

const errorMessage = (e: unknown) => (e instanceof Error ? e.message : 'Someone changed this entry since you opened it. Review the latest and try again.');

/** Prototype respModal: "Your side". */
function RespondModal({ i, onClose }: { i: RecordEntry; onClose: () => void }) {
  const m = useRespondToIncident();
  const { errs, setErrs, fail, toast } = useFail(['text']);
  const [text, setText] = useState(i.response?.text ?? '');
  const save = () => {
    setErrs({});
    const t = text.trim();
    if (!t) {
      setErrs({ text: 'Write a line first' });
      return;
    }
    m.mutate(
      { id: i.id, version: i.version, body: { text: t } },
      {
        onSuccess: () => {
          toast('Added. The Ops associate director can see it.');
          onClose();
        },
        onError: (e) => {
          if (stale(e)) {
            toast(errorMessage(e));
            onClose();
          } else fail(e);
        },
      },
    );
  };
  return (
    <Modal
      title="Your side"
      sub={i.title}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={m.isPending}>
            {m.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <Field label="What happened from your point of view, and what you changed" error={errs.text}>
        <textarea className="in" rows={4} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} autoFocus />
      </Field>
    </Modal>
  );
}

/** Prototype disModal: "Dismiss this entry". */
function DismissModal({ i, onClose }: { i: RecordEntry; onClose: () => void }) {
  const m = useDismissIncident();
  const { errs, setErrs, fail, toast } = useFail(['reason']);
  const [reason, setReason] = useState('');
  const save = () => {
    setErrs({});
    const r = reason.trim();
    if (!r) {
      setErrs({ reason: 'A reason is needed' });
      return;
    }
    m.mutate(
      { id: i.id, version: i.version, body: { reason: r } },
      {
        onSuccess: () => {
          toast('Dismissed and kept in the record');
          onClose();
        },
        onError: (e) => {
          if (stale(e)) {
            toast(errorMessage(e));
            onClose();
          } else fail(e);
        },
      },
    );
  };
  return (
    <Modal
      title="Dismiss this entry"
      sub={
        <>
          {i.title}
          <br />
          <span className="sub">It stays in the record, marked dismissed, with your reason.</span>
        </>
      }
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn bad" onClick={save} disabled={m.isPending}>
            {m.isPending ? 'Dismissing…' : 'Dismiss'}
          </button>
        </>
      }
    >
      <Field label="Why" error={errs.reason}>
        <input
          className="in"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => (e.key === 'Enter' ? save() : undefined)}
          placeholder="e.g. Logged twice, or not related to our work"
          maxLength={500}
          autoFocus
        />
      </Field>
    </Modal>
  );
}

/** Prototype incModal: "Log an incident" — for anything the system could not notice, or from before it was used. */
function LogIncidentModal({ people, onClose }: { people: UserRef[]; onClose: () => void }) {
  const m = useCreateIncident();
  const clients = useClients();
  const today = todayIso();
  const { errs, setErrs, fail, toast } = useFail(['title', 'kind', 'occurred_on', 'client_id', 'owner_id', 'cause', 'severity', 'note', 'outcome']);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<IncidentKind>('mistake');
  const [day, setDay] = useState(today);
  const [client, setClient] = useState('');
  const [owner, setOwner] = useState(people[0]?.id ?? '');
  const [cause, setCause] = useState<IncidentCause>('person');
  const [sev, setSev] = useState<Severity>('med');
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState('');
  const save = () => {
    setErrs({});
    const t = title.trim();
    if (!t || !day) {
      setErrs({ ...(t ? {} : { title: 'Say what happened' }), ...(day ? {} : { occurred_on: 'Pick the date' }) });
      return;
    }
    if (day > today) {
      setErrs({ occurred_on: 'Today or earlier' });
      return;
    }
    const n = note.trim();
    if (cause === 'person' && !n) {
      setErrs({ note: 'Say what went wrong, so the person can see it' });
      return;
    }
    const out = outcome.trim();
    m.mutate(
      {
        kind,
        title: t,
        client_id: client || null,
        owner_id: owner,
        occurred_on: day,
        severity: sev,
        cause,
        ...(cause === 'person' ? { caused_by_id: owner } : {}),
        ...(n ? { note: n } : {}),
        ...(out ? { outcome: out } : {}),
      },
      {
        onSuccess: (r) => {
          toast(`Logged in ${who1(r.owner ?? people.find((u) => u.id === owner))}’s record`);
          onClose();
        },
        onError: fail,
      },
    );
  };
  return (
    <Modal
      title="Log an incident"
      sub="For anything the system could not notice, or something from before it was used."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={m.isPending || !owner}>
            {m.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <Field label="What happened" error={errs.title}>
        <input className="in" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} autoFocus />
      </Field>
      <div className="cols c2">
        <Field label="Type" error={errs.kind}>
          <select className="in" value={kind} onChange={(e) => setKind(e.target.value as IncidentKind)}>
            {(Object.keys(IKIND) as IncidentKind[]).map((k) => (
              <option key={k} value={k}>
                {IKIND[k]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Date" error={errs.occurred_on}>
          <input className="in" type="date" value={day} max={today} onChange={(e) => setDay(e.target.value)} />
        </Field>
      </div>
      <div className="cols c2">
        <Field label="Client" error={errs.client_id}>
          <select className="in" value={client} onChange={(e) => setClient(e.target.value)} disabled={clients.isPending}>
            <option value="">{clients.isPending ? 'Loading clients…' : 'Internal'}</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="On whose watch" error={errs.owner_id}>
          <select className="in" value={owner} onChange={(e) => setOwner(e.target.value)}>
            {people.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="cols c2">
        <Field label="Cause" error={errs.cause}>
          <select className="in" value={cause} onChange={(e) => setCause(e.target.value as IncidentCause)}>
            {(Object.keys(CAUSES) as IncidentCause[]).map((k) => (
              <option key={k} value={k}>
                {CAUSES[k][1]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="How serious" error={errs.severity}>
          <select className="in" value={sev} onChange={(e) => setSev(e.target.value as Severity)}>
            <option value="med">Medium</option>
            <option value="high">High</option>
            <option value="low">Low</option>
          </select>
        </Field>
      </div>
      <Field label="What went wrong" error={errs.note}>
        <textarea className="in" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
      </Field>
      <Field label="Outcome (blank if still open)" error={errs.outcome}>
        <input className="in" value={outcome} onChange={(e) => setOutcome(e.target.value)} maxLength={2000} />
      </Field>
    </Modal>
  );
}
